import React, { useState, useEffect } from 'react';
import { LuLandmark, LuMapPin, LuPhone, LuLoader, LuMessageCircle } from 'react-icons/lu';
import { GiChurch } from 'react-icons/gi';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';

export default function SocialBoard() {
  const [boardItems, setBoardItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchSocialBoard() {
      try {
        setLoading(true);
        let querySnapshot;
        try {
          querySnapshot = await getDocs(collection(db, 'social-board'));
          if (querySnapshot.empty) {
            querySnapshot = await getDocs(collection(db, 'social_board'));
          }
          if (querySnapshot.empty) {
            querySnapshot = await getDocs(collection(db, 'socialboard'));
          }
        } catch (e) {
          try {
            querySnapshot = await getDocs(collection(db, 'social_board'));
          } catch (e2) {
            querySnapshot = await getDocs(collection(db, 'social-board'));
          }
        }

        const fetched = [];
        if (querySnapshot && !querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const name = data.socialboard_name || data.name || data.title || "C.S.I Resource Center";
            const district = data.district || "WAYANAD";
            const state = data.state || "KERALA";
            const placeRaw = data.place || data.address || "Sultan Bathery";
            const order = typeof data.order === 'number' ? data.order : 999;

            // Extract phone if embedded in place text (e.g. "SULTAN BATHERY, PHN: 9946494158")
            let phone = data.phone || data.mobile || data.contact || "";
            let cleanPlace = placeRaw;

            if (!phone) {
              const phoneMatch = placeRaw.match(/(?:PHN|PH|PHONE|TEL|MOB|MOBILE)?[:\s-]*(\+?\d[\d\s-]{8,14}\d)/i);
              if (phoneMatch) {
                phone = phoneMatch[1].replace(/\s+/g, '');
                cleanPlace = placeRaw.replace(phoneMatch[0], '').replace(/[,-/\s]+$/, '').trim();
              }
            }

            fetched.push({
              id: doc.id,
              name: name.toUpperCase(),
              district,
              state,
              place: cleanPlace || placeRaw,
              phone,
              order,
              raw: data
            });
          });

          fetched.sort((a, b) => a.order - b.order);
        }

        if (isMounted) {
          setBoardItems(fetched);
        }
      } catch (err) {
        console.error("Error fetching social board data from Firebase:", err);
        if (isMounted) setBoardItems([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchSocialBoard();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#5F5A58] font-sans flex flex-col pb-16">
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-b from-[#FAF6F2] via-[#F5EFEE] to-[#E8DDD7] pt-28 sm:pt-32 pb-16 overflow-hidden border-b border-[#E8DDD7]">
        <div className="absolute right-[5%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none">
          <GiChurch className="w-64 h-64 sm:w-96 sm:h-96 text-[#7B1E3A]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 flex flex-col items-center md:items-start text-center md:text-left">
          <div className="w-16 h-1 bg-[#D4A64A] mb-6 rounded-full" />
          <h1 className="font-serif font-bold text-[#7B1E3A] text-[38px] sm:text-[50px] lg:text-[56px] uppercase tracking-[0.03em] leading-[1.05]">
            Diocesan Social Board
          </h1>
          <p className="mt-4 max-w-xl text-[#7B6B66] text-[16px] sm:text-[17px] leading-[1.6]">
            Community development, ecological sustainability, climate justice labs, and welfare missions of the CSI Diocese of Malabar.
          </p>
        </div>
      </section>

      {/* Items Grid */}
      <div className="max-w-[1440px] mx-auto px-6 sm:px-8 mt-12 w-full flex-grow">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-gray-100 shadow-sm">
            <LuLoader className="w-10 h-10 text-[#842343] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Connecting to Firebase & loading social board...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {boardItems.map((item, idx) => {
              const cleanDigits = item.phone ? item.phone.replace(/\D/g, '') : '';
              const fullLocation = [item.place, item.district, item.state].filter(Boolean).join(', ');

              return (
                <div
                  key={item.id || idx}
                  className="bg-white rounded-[28px] border border-[#E8DDD7] shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_16px_45px_rgba(123,30,58,0.08)] hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between p-8 sm:p-9 group h-full relative overflow-hidden"
                >
                  {/* Decorative corner background */}
                  <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-[#FAF0F2] via-transparent to-transparent rounded-bl-full opacity-60 pointer-events-none group-hover:scale-110 transition-transform duration-500" />

                  <div className="relative z-10">
                    {/* Header Row with Icon & Badges */}
                    <div className="flex items-center justify-between gap-3 mb-6">
                      <div className="w-14 h-14 rounded-2xl bg-[#842343] text-white flex items-center justify-center shadow-md shrink-0">
                        <LuLandmark className="w-7 h-7" />
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {item.district && (
                          <span className="text-[11px] font-bold uppercase tracking-wider text-[#842343] bg-[#842343]/10 px-3 py-1 rounded-full">
                            {item.district}
                          </span>
                        )}
                        {item.state && (
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#8A7E79] bg-[#FAF7F2] border border-[#E8DDD7] px-2.5 py-1 rounded-full">
                            {item.state}
                          </span>
                        )}
                      </div>
                    </div>

                    <h3 className="font-serif font-bold text-[#842343] text-xl sm:text-2xl leading-snug mb-5">
                      {item.name}
                    </h3>
                  </div>
                  
                  {/* Bottom Contact & Location Card */}
                  <div className="relative z-10 space-y-4 pt-6 border-t border-[#F0EBE6] mt-4">
                    {/* Contact Helpline */}
                    {item.phone && (
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#FAF7F2] p-4 rounded-2xl border border-[#E8DDD7]">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-[#842343] shrink-0 shadow-2xs border border-[#E8DDD7]">
                            <LuPhone className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Helpline / Coordinator</p>
                            <a href={`tel:${item.phone}`} className="text-[#842343] font-bold text-sm sm:text-base hover:underline">
                              {item.phone}
                            </a>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <a
                            href={`tel:${item.phone}`}
                            className="bg-[#842343] hover:bg-[#6c1b35] text-white text-xs font-bold py-2 px-3.5 rounded-xl shadow-2xs transition-colors flex items-center gap-1.5"
                          >
                            <LuPhone className="w-3.5 h-3.5" />
                            <span>Call</span>
                          </a>
                          {cleanDigits && (
                            <a
                              href={`https://wa.me/91${cleanDigits}`}
                              target="_blank"
                              rel="noreferrer"
                              className="bg-[#25D366] hover:bg-[#20bd5a] text-white text-xs font-bold py-2 px-3.5 rounded-xl shadow-2xs transition-colors flex items-center gap-1.5"
                            >
                              <LuMessageCircle className="w-3.5 h-3.5" />
                              <span>WhatsApp</span>
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Location Address */}
                    <div className="flex items-start gap-3 text-[#5F5A58] text-[13.5px] px-1">
                      <div className="w-8 h-8 rounded-full bg-[#FAF7F2] flex items-center justify-center shrink-0 mt-0.5 border border-[#E8DDD7]">
                        <LuMapPin className="w-4 h-4 text-[#D4A64A]" />
                      </div>
                      <span className="leading-relaxed font-medium mt-1">
                        {fullLocation}
                      </span>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}
